import React, { useState, useEffect } from "react";
import { RefreshCw, Loader2, AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

// Step 3 — Produk & Rekomendasi Varian. Konsumsi POST /production/plans/recommend (preview-only).
// Final qty editable; override wajib alasan; simpan snapshot immutable (audit) sebelum diterapkan.
// props: productIds[], color, fabricBudgetYard, onApply(rowsWithFinalQty). Frontend TIDAK menghitung angka bisnis.
const num = (n) => (Number(n) || 0).toLocaleString("id-ID");
const genKey = () => `reco-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const rowKey = (r) => `${r.product_id}|${r.color || ""}|${r.size || ""}`;

export function PlanRecoStep({ productIds = [], color = "", fabricBudgetYard = 0, onApply }) {
  const [params, setParams] = useState({ period: 90, lead_time_days: 14, cover_days: 30, safety_days: 7, allowance_pct: 3 });
  const [state, setState] = useState("idle"); // idle|loading|ok|empty|error
  const [data, setData] = useState(null);
  const [stale, setStale] = useState(false);
  const [showBasis, setShowBasis] = useState(false);
  const [finals, setFinals] = useState({}); // size -> final qty (editable)
  const [reason, setReason] = useState("");
  const [idemKey, setIdemKey] = useState(null);
  const [saving, setSaving] = useState(false);

  // Perubahan input/param menandai hasil lama stale.
  useEffect(() => { setStale(true); }, [JSON.stringify(productIds), color, fabricBudgetYard, JSON.stringify(params)]);

  const compute = async () => {
    if (!productIds.length || !fabricBudgetYard) { toast.error("Pilih produk & pastikan roll/kain (budget yard) terisi."); return; }
    setState("loading");
    try {
      const r = await api.post("/production/plans/recommend", { product_ids: productIds, color, fabric_budget_yard: fabricBudgetYard, ...params });
      setData(r.data); setStale(false); setReason(""); setIdemKey(genKey());
      const f = {}; (r.data.rows || []).forEach((x) => { f[rowKey(x)] = x.suggested_qty; });
      setFinals(f);
      setState((r.data.rows || []).length ? "ok" : "empty");
    } catch (e) { setState("error"); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const overridden = data ? (data.rows || []).some((x) => (Number(finals[rowKey(x)]) || 0) !== x.suggested_qty) : false;

  const apply = async () => {
    if (!data?.rows?.length) return;
    if (overridden && !reason.trim()) { toast.error("Final berbeda dari saran — isi alasan override dulu."); return; }
    const rows = data.rows.map((x) => ({ ...x, final_qty: Number(finals[rowKey(x)]) || 0 }));
    setSaving(true);
    try {
      // Stage 4 — snapshot immutable (audit-only, idempotent). Tidak mereservasi stok/plan.
      const snap = await api.post("/production/plans/reco-snapshots", {
        idempotency_key: idemKey || genKey(), as_of: data.as_of, formula_version: data.formula_version,
        source: data.source, params: data.params,
        rows: rows.map((x) => ({ size: x.size, product_id: x.product_id, color: x.color, sku: x.sku,
          net_requirement: x.net_requirement, suggested_qty: x.suggested_qty, final_qty: x.final_qty,
          base_stock_at_ready: x.base_stock_at_ready, incoming_eligible: x.incoming_eligible, velocity: x.velocity })),
        totals: data.totals, explanations: data.explanations, assumptions: data.assumptions, warnings: data.warnings,
        override_reason: reason.trim(), product_ids: productIds, color, fabric_budget_yard: fabricBudgetYard,
      });
      if (onApply) onApply(rows);
      toast.success(`Snapshot ${snap.data.number} tersimpan · saran disalin ke varian`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="plan-reco-step">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[["period", "Periode sales (hari)"], ["cover_days", "Target cover (hari)"], ["lead_time_days", "Lead time (hari)"], ["safety_days", "Safety (hari)"], ["allowance_pct", "Allowance (%)"]].map(([k, lbl]) => (
          <div key={k}><Label className="text-xs">{lbl}</Label>
            <Input type="number" className="h-8" value={params[k]} onChange={(e) => setParams({ ...params, [k]: parseFloat(e.target.value) || 0 })} data-testid={`reco-param-${k}`} /></div>
        ))}
      </div>
      <Button onClick={compute} disabled={state === "loading"} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="reco-compute-btn">
        {state === "loading" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}{data ? "Hitung Ulang" : "Hitung Rekomendasi"}
      </Button>
      {stale && data && <span className="ml-2 text-xs text-amber-700" data-testid="reco-stale">Input berubah — hitung ulang agar hasil valid.</span>}

      {state === "loading" && <div className="text-center text-wmuted py-8" data-testid="reco-loading"><Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />Menghitung rekomendasi…</div>}
      {state === "error" && <div className="text-center text-status-reject py-8" data-testid="reco-error"><AlertTriangle className="w-5 h-5 mx-auto mb-2" />Gagal menghitung. <button onClick={compute} className="underline">Coba lagi</button></div>}
      {state === "empty" && <div className="text-center text-wmuted py-8" data-testid="reco-empty">Tidak ada SKU cocok / data penjualan tidak cukup. {(data?.warnings || []).join(" ")}</div>}

      {state === "ok" && data && (
        <>
          <div className={`rounded-lg border ${stale ? "border-amber-300 opacity-70" : "border-taupe/20"} overflow-auto max-h-[50vh]`}>
            <Table>
              <TableHeader className="sticky top-0 bg-ivory">
                <TableRow><TableHead>Produk / Size</TableHead><TableHead className="text-right">Jual/{data.source?.period_days}h</TableHead><TableHead className="text-right">Velocity</TableHead><TableHead className="text-right">Available</TableHead><TableHead className="text-right">WIP</TableHead><TableHead className="text-right">Proy. saat ready</TableHead><TableHead className="text-right">Butuh</TableHead><TableHead className="text-right">Saran</TableHead><TableHead className="text-right">Final</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={`${r.product_id}-${r.color}-${r.size}`} data-testid={`reco-row-${r.size}`}>
                    <TableCell className="font-medium text-cocoa"><div className="text-[11px] text-wmuted">{r.color || "-"}</div>{r.size}</TableCell>
                    <TableCell className="text-right">{num(r.sales_period_qty)}</TableCell>
                    <TableCell className="text-right">{(Number(r.velocity) || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{num(r.available)}</TableCell>
                    <TableCell className="text-right">{num(r.incoming_eligible)}</TableCell>
                    <TableCell className="text-right">{num(r.base_stock_at_ready)}</TableCell>
                    <TableCell className="text-right">{num(r.net_requirement)}</TableCell>
                    <TableCell className="text-right font-semibold text-cocoa">{num(r.suggested_qty)}</TableCell>
                    <TableCell className="text-right">
                      <Input type="number" className={`h-7 w-20 ml-auto text-right ${(Number(finals[rowKey(r)]) || 0) !== r.suggested_qty ? "border-amber-400" : ""}`} value={finals[rowKey(r)] ?? r.suggested_qty} onChange={(e) => setFinals((s) => ({ ...s, [rowKey(r)]: parseInt(e.target.value) || 0 }))} data-testid={`reco-final-${r.color || "x"}-${r.size}`} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-sm text-wmuted">Total saran {num(data.totals?.total_pcs)} pcs · kain {num(data.totals?.base_fabric_yard)} yd (+allowance {num(data.totals?.fabric_with_allowance_yard)} yd) · sisa {num(data.totals?.fabric_remaining_yard)} yd · kekurangan {num(data.totals?.total_unmet_requirement)} pcs</div>

          <div className="rounded-lg bg-taupe/10 p-3" data-testid="reco-why">
            <div className="font-heading text-cocoa text-sm mb-1">Mengapa komposisi ini?</div>
            {(data.explanations || []).map((e) => <div key={e.size} className="text-xs text-cocoa/80 mb-0.5">• {e.text}</div>)}
          </div>

          <button onClick={() => setShowBasis((s) => !s)} className="text-xs text-cocoa underline inline-flex items-center gap-1" data-testid="reco-basis-toggle"><Info className="w-3.5 h-3.5" />Lihat Dasar Perhitungan</button>
          {showBasis && (
            <div className="text-xs text-wmuted space-y-1 border-l-2 border-taupe/30 pl-3" data-testid="reco-basis">
              <div>as_of: {data.as_of} · formula: {data.formula_version} · scope: {data.source?.warehouse}/{data.source?.channel} · varian cocok: {data.source?.matched_variants}</div>
              <div>Parameter: {JSON.stringify(data.params)}</div>
              {(data.assumptions || []).map((a, i) => <div key={i}>• Asumsi: {a}</div>)}
              {(data.warnings || []).map((w, i) => <div key={i} className="text-amber-700">⚠ {w}</div>)}
              <div className="italic">Hasil adalah estimasi cutting, bukan jaminan hasil pola / barang lolos QC.</div>
            </div>
          )}

          {overridden && (
            <div data-testid="reco-override">
              <Label className="text-xs text-amber-700">Alasan override (Final ≠ Saran) *</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: menyesuaikan permintaan reseller / batas budget kain aktual" data-testid="reco-override-reason" />
            </div>
          )}

          {onApply && <Button onClick={apply} disabled={stale || saving} variant="outline" className="border-cocoa text-cocoa" data-testid="reco-apply-btn">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}Gunakan Rekomendasi (isi varian + simpan snapshot)</Button>}
        </>
      )}
    </div>
  );
}
